// eslint-disable-next-line @tseslint/no-shadow
import { AfterViewInit, ChangeDetectionStrategy, Component, contentChild, DestroyRef, EventEmitter, inject, input, InputSignal, model, ModelSignal, Output, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { InlineConfirmationConfirmDirective } from './directives/inline-confirmation-confirm.directive';
import { InlineConfirmationCancelDirective } from './directives/inline-confirmation-cancel.directive';
import { InlineConfirmationTriggerDirective } from './directives/inline-confirmation-trigger.directive';

@Component({
  selector: 'app-inline-confirmation',
  standalone: true,
  host: {
    "[class.inline-confirmation--open]": "isOpen()"
  },
  imports: [],
  templateUrl: './inline-confirmation.component.html',
  styleUrl: './inline-confirmation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InlineConfirmationComponent implements AfterViewInit {
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  public readonly trigger: InputSignal<InlineConfirmationTriggerDirective | undefined> = input<InlineConfirmationTriggerDirective | undefined>();

  /** If the confirmation should close after the user either confirms or cancels */
  public readonly shouldCloseOnEmit: InputSignal<boolean> = input(true);

  public readonly isOpen: ModelSignal<boolean> = model(false);

  @Output() public readonly cancelEvent: EventEmitter<PointerEvent> = new EventEmitter<PointerEvent>();

  @Output() public readonly confirm: EventEmitter<PointerEvent> = new EventEmitter<PointerEvent>();

  private readonly confirmElement: Signal<InlineConfirmationConfirmDirective | undefined> = contentChild(InlineConfirmationConfirmDirective);

  private readonly cancelElement: Signal<InlineConfirmationCancelDirective | undefined> = contentChild(InlineConfirmationCancelDirective);

  public ngAfterViewInit (): void {
    const cancelElement: InlineConfirmationCancelDirective | undefined = this.cancelElement();
    const confirmElement: InlineConfirmationConfirmDirective | undefined = this.confirmElement();

    if (cancelElement) {
      cancelElement.cancel$
        .pipe(
          takeUntilDestroyed(this._destroyRef)
        ).subscribe((pointerEvent: PointerEvent) => {
          this.cancelEvent.emit(pointerEvent);

          if (this.shouldCloseOnEmit())
            this.isOpen.set(false);
        });
    }

    if (confirmElement) {
      confirmElement.confirm$
        .pipe(
          takeUntilDestroyed(this._destroyRef)
        ).subscribe((pointerEvent: PointerEvent) => {
          this.confirm.emit(pointerEvent);

          if (this.shouldCloseOnEmit())
            this.isOpen.set(false);
        });
    }

    const trigger: InlineConfirmationTriggerDirective | undefined = this.trigger();

    if (trigger) {
      fromEvent(trigger.elementRef.nativeElement, "click")
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          this.isOpen.set(!this.isOpen());
        });
    }
  }
}
